import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'
import type { CicloOrden, IndicadoresCotizacion } from '@/lib/supabase/tipos-bd'

/**
 * Los relojes de la matriz.
 *
 * Toda orden de servicio debería terminar cotizada. La que no lo está no es un
 * pendiente: es plata quieta. Aquí se mide cuánto lleva quieta y cuánto suele
 * tardar el paso de orden a cotización.
 */

/**
 * Cuándo una espera deja de ser normal.
 *
 * ESTABAN EN 24 Y 72 HORAS Y ERA UN ERROR. Cotizar exige que alguien entre a
 * World Office, arme el documento y lo devuelva: un día es el curso normal del
 * trabajo, no un retraso. Con el umbral en 24 horas, las cuatro órdenes sin
 * cotizar de MEGA estaban las cuatro en aviso — y cuando todo está en aviso,
 * el color deja de querer decir nada y el tablero solo se ve alarmado.
 *
 * Dos días para preocuparse, cuatro para que sea un problema. Con eso, el
 * naranja vuelve a señalar en vez de decorar.
 */
export const HORAS_AVISO = 48
export const HORAS_ALARMA = 96

export function useIndicadoresCotizacion() {
  const consulta = useQuery({
    queryKey: ['indicadores-cotizacion', HORAS_AVISO, HORAS_ALARMA],
    // Por si la conexión en vivo se cae sin avisar: cada minuto, solo.
    refetchInterval: 60 * 1000,
    // Un minuto: los relojes corren, pero nadie mira el tablero al segundo.
    staleTime: 60 * 1000,
    queryFn: async (): Promise<IndicadoresCotizacion | null> => {
      const { data, error } = await supabase.rpc('fn_indicadores_cotizacion', {
        p_horas_aviso: HORAS_AVISO,
        p_horas_alarma: HORAS_ALARMA,
      })

      if (error) throw new Error(mensajeDeError(error))

      const fila = (data as unknown as IndicadoresCotizacion[] | null)?.[0]
      if (!fila) return null

      return {
        ...fila,
        espera_mediana_horas: numero(fila.espera_mediana_horas),
        espera_maxima_horas: numero(fila.espera_maxima_horas),
        demora_mediana_horas: numero(fila.demora_mediana_horas),
        demora_promedio_horas: numero(fila.demora_promedio_horas),
        demora_maxima_horas: numero(fila.demora_maxima_horas),
        cliente_mediana_dias: numero(fila.cliente_mediana_dias),
      }
    },
  })

  return {
    indicadores: consulta.data ?? null,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/** Las órdenes que siguen sin precio, de la más vieja a la más nueva. */
export function useOrdenesEsperando() {
  const consulta = useQuery({
    queryKey: ['ordenes-esperando'],
    // Por si la conexión en vivo se cae sin avisar: cada minuto, solo.
    refetchInterval: 60 * 1000,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CicloOrden[]> => {
      const { data, error } = await supabase
        .from('vista_ciclo_ordenes')
        .select('*')
        .not('horas_esperando', 'is', null)
        .order('horas_esperando', { ascending: false })

      if (error) throw new Error(mensajeDeError(error))

      return (data ?? []) as unknown as CicloOrden[]
    },
  })

  return { ordenes: consulta.data ?? [], cargando: consulta.isLoading }
}

/** PostgREST devuelve los numeric como texto: se convierten en un solo sitio. */
function numero(valor: number | string | null): number | null {
  return valor === null ? null : Number(valor)
}
